import { useMediaQuery, useTheme } from '@mui/material';
import { useDebounce } from '@uidotdev/usehooks';
import queryString from 'query-string';
import React from 'react';
import * as Space from 'react-spaces';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import AccessConsentPage from '../Components/AccessConsentPage';
import Authenticator from './Authenticator';
import PluginToolbar from './Components/AppBar';
import AudioWarning from './Components/AudioWarning';
import Header from './Components/Header';
import MainWindow from './Components/MainWindow';
import NotificationSnackbarReminder from './Components/NotificationSnackbarReminder';
import QuickAccessBar from './Components/QuickAccessBar';
import SessionPanel from './Components/SessionPanel';
import DetectionGroupContextProvider from './Components/SessionPanel/Contexts/DetectionGroupContext/DetectionGroupContextProvider';
import StyledSnackbarProvider from './Components/StyledSnackbarProvider';
import BlackListLinkContextProvider from './Contexts/BlackListLinkContextProvider';
import DecisionTreeContextProvider from './Contexts/DecisionTree/DecisionTreeContext/DecisionTreeContextProvider';
import GlobalContextProvider from './Contexts/GlobalContext/GlobalContextProvider';
import LiveWysiwygHost from '../Common/WysiwygHost';
import preloadTranslations from './Hooks/audio/preloadTranslations';
import useIsSessionLivePage from './Hooks/useIsSessionLivePage';
import AudioConfigContextProvider from './LiveContext/AudioConfigContext/AudioConfigContextProvider';
import MultiLanguageContextProvider from './LiveContext/MultiLanguageContext/MultiLanguageContextProvider';
import SessionContextProvider from './LiveContext/SessionContextProvider';
import useSessionContext from './LiveContext/SessionContextProvider/useSessionContext';
import StyledBox, { renderStyledHandler } from './styles';
import Suspense from './Suspense';

function InnerLivePage(props: { setRef: React.Dispatch<React.SetStateAction<HTMLDivElement | null | undefined>> }) {
  const { setRef } = props;
  const { session } = useSessionContext();
  const isSessionLivePage = useIsSessionLivePage();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));

  const [notificationPermission, setNotificationPermission] = React.useState<string>('default');
  const [tab, setTab] = React.useState({ index: 0, updatedAt: Date.now() });

  React.useEffect(() => {
    preloadTranslations();
  }, []);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      Notification.requestPermission()
        .then((permission) => {
          setNotificationPermission(permission);
        })
        // Rejects with NotAllowedError when the prompt is dismissed rather than
        // answered. That is an ordinary outcome and leaves the state at 'default'.
        .catch(() => {});
    }
  }, []);

  const shouldMountSessionPanel = React.useMemo(() => !!session && !session.isInactiveSession && !isSessionLivePage && !isXs, [session, isSessionLivePage, isXs]);
  const debouncedShouldMountSessionPanel = useDebounce(shouldMountSessionPanel, 200);

  return (
    <StyledBox>
      <DetectionGroupContextProvider tab={tab}>
        <Space.Top size={48}>
          <Suspense>
            <Header />
          </Suspense>
        </Space.Top>
        <Space.Fill>
          <Suspense>
            <QuickAccessBar />
          </Suspense>
          <Space.Fill>
            <Space.Fill id="top-pane">
              <Space.Fill id="main-pane">
                <Suspense>
                  <MainWindow setRef={setRef} />
                </Suspense>
              </Space.Fill>
              {debouncedShouldMountSessionPanel && (
                <Suspense>
                  <Space.RightResizable size={270} maximumSize={400} minimumSize={40} handleRender={renderStyledHandler} id="session-pane">
                    <SessionPanel key="detection" tab={tab} setTab={setTab} />
                  </Space.RightResizable>
                </Suspense>
              )}
            </Space.Fill>
            <Suspense>
              <NotificationSnackbarReminder notificationPermission={notificationPermission} />
            </Suspense>
            <Suspense>
              <AudioWarning />
            </Suspense>
          </Space.Fill>
        </Space.Fill>
        <Space.Bottom size={75}>
          <Suspense>
            <PluginToolbar tab={tab} setTab={setTab} />
          </Suspense>
        </Space.Bottom>
      </DetectionGroupContextProvider>
    </StyledBox>
  );
}

export default function App() {
  const [ref, setRef] = React.useState<HTMLDivElement | null | undefined>(null);

  return (
    <Authenticator>
      <QueryParamProvider
        adapter={ReactRouter6Adapter}
        options={{
          searchStringToObject: queryString.parse,
          objectToSearchString: queryString.stringify,
        }}
      >
        <Suspense>
          <AccessConsentPage>
            <BlackListLinkContextProvider>
              <StyledSnackbarProvider domRoot={ref ?? undefined}>
                <GlobalContextProvider>
                  <AudioConfigContextProvider>
                    <MultiLanguageContextProvider>
                      <SessionContextProvider>
                        <DecisionTreeContextProvider>
                          <LiveWysiwygHost>
                            <InnerLivePage setRef={setRef} />
                          </LiveWysiwygHost>
                        </DecisionTreeContextProvider>
                      </SessionContextProvider>
                    </MultiLanguageContextProvider>
                  </AudioConfigContextProvider>
                </GlobalContextProvider>
              </StyledSnackbarProvider>
            </BlackListLinkContextProvider>
          </AccessConsentPage>
        </Suspense>
      </QueryParamProvider>
    </Authenticator>
  );
}
